<script lang="ts">
import { defineComponent, PropType, inject, computed, watch, onBeforeUnmount } from 'vue'
import ERROR_TYPES from '@/constants/errorTypes'
import { VALIDATION_DEFAULT_MESSAGES } from '@/constants/validationMessages'
import { VALIDATION_OBSERVER_KEY } from '@/components/validation/context'
import { validateValue, hasValidationScheme } from '@/utilities/validateValue'
import { useErrorStore } from '@/stores/error'
import { useToastStore } from '@/stores/toast'
import { datadogService } from '@/services/datadog'
import type { ValidationFieldHandle } from '@/components/validation/context'

export default defineComponent({
  name: 'ValidationProvider',

  props: {
    /**
     * 検査対象の value
     */
    value: {
      type: [String, Number, Boolean, Array, File] as PropType<InputValue | undefined>,
      default: undefined,
    },

    /**
     * チェックボックス等の選択形式のフォームにおいて、未選択時の value が明示的に存在する場合の未選択値
     * @description チェックボックス等は、false-value オプションが付与できる（未選択時の値）。未選択時にも明示的に値が設定されるものに関しては、未選択 = 空欄 にはならないため、required 判定が抜けてしまうため、false value との比較を行う。
     */
    falseValue: {
      type: [String, Number, Boolean],
      default: undefined,
    },

    /**
     * バリデーションの名前空間
     * @description 直近の祖先にある ValidationObserver の namespace と一致する場合のみ、その Observer の検査対象として登録される。
     * @description Form submit の場合、form の name 属性の値にする。
     */
    namespace: {
      type: String,
      default: '',
    },

    /**
     * store にエラーメッセージを登録する際のフィールド名
     */
    field: {
      type: String,
      default: '',
    },

    /**
     * 有効にするバリデーションの設定
     */
    scheme: {
      type: Object as PropType<ValidationScheme>,
      default: () => ({
        /**
         * validation scheme examples
         */
        required: false,
        email: false,
        url: false,
        password: false,
        number: false,
        kana: false,
        date: false,
        phoneNumber: false,
        maxLength: 0,
        minLength: 0,
      }),
    },

    /**
     * バリデーションエラー時に表示するメッセージ定義
     * @description 定義を注入しなかった場合、デフォルトの文言が表示される。
     */
    messages: {
      type: Object as PropType<ValidationMessages>,
      default: () => ({
        ...VALIDATION_DEFAULT_MESSAGES,
      }),
    },

    /**
     * エラーメッセージを表示するかどうか
     *
     * @description
     *  バリデーションはしたいが、エラーメッセージは隠したい場合（hidden 要素等）、
     *  特に、「勤務先名」のように入力フィールドに付随する hidden 要素が存在する場合において、入力フィールドの入力状況によって hidden 要素の方のエラーを表示するかどうか制御するような状況で使用する。
     *  バリデーションエラーメッセージのアニメーションの兼ね合いで DOM が存在していないといけないため、v-if 制御ではなく、CSS 側での表示制御とする
     */
    showMessage: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['error', 'start', 'completed'],

  setup(props, { emit }) {
    // constants
    const INVALID_MAIL_DOMAINS = inject<string[]>('INVALID_MAIL_DOMAINS') || []

    const observer = inject(VALIDATION_OBSERVER_KEY, undefined)

    const errorStore = useErrorStore()
    const toastStore = useToastStore()

    /**
     * バリデーションが設定されているかどうか
     */
    const hasValidation = computed<boolean>(() => hasValidationScheme(props.scheme))

    /**
     * 検査を実行し、結果を store に反映する
     * @see @/utilities/validateValue.ts
     */
    const validate = async (): Promise<boolean> => {
      emit('start')

      const result = await validateValue(props.value, props.scheme, props.messages, {
        falseValue: props.falseValue,
        invalidMailDomains: INVALID_MAIL_DOMAINS,
      })

      if (!result.valid) {
        errorStore.setError(ERROR_TYPES.VALIDATION_ERROR, { field: props.field, messages: [result.message] }, props.namespace)

        if (result.notice) {
          toastStore.setToastItem({
            id: `toast-${new Date().getTime()}`,
            messageType: 'alert',
            message: result.notice,
          })
        }

        emit('error', props.field)
      }

      emit('completed', props.field)
      return result.valid
    }

    const handle: ValidationFieldHandle = {
      get field() {
        return props.field
      },
      validate,
    }

    /**
     * 直近の ValidationObserver の検査対象として登録するかどうか
     * @description バリデーション設定があり、field が確定していて、Observer と namespace が一致するものだけを登録する。
     */
    const isRegistrable = computed<boolean>(() => {
      if (!observer) return false
      return hasValidation.value && !!props.field && props.namespace === observer.namespace.value
    })

    /**
     * 検査設定があるのに登録先が無い Provider を記録する
     * @description 登録漏れは「静かに未検査のまま送信される」fail-open なので、開発時のコンソール警告に加えて本番でも Datadog に痕跡を残す。
     *              spec は Provider を単体でマウントすることがあるため test モードでは何もしない。
     */
    const reportUnregistered = () => {
      if (import.meta.env.MODE === 'test') return

      const reason = observer
        ? `namespace "${props.namespace}" が直近の ValidationObserver（"${observer.namespace.value}"）と一致しません`
        : '祖先に ValidationObserver がありません'
      const message = `[ValidationProvider] "${props.field}" は検査対象に登録されていません（${reason}）`

      if (import.meta.env.MODE !== 'production') console.warn(message)

      datadogService.trackError(new Error(message), {
        error_type: 'validation_provider_unregistered',
        field: props.field,
        namespace: props.namespace,
        observer_namespace: observer?.namespace.value ?? null,
      }, 'warn')
    }

    watch(isRegistrable, (registrable, wasRegistrable) => {
      if (registrable) {
        observer!.register(handle)
      } else if (wasRegistrable) {
        observer!.unregister(handle)
      }

      if (!registrable && hasValidation.value && props.field) reportUnregistered()
    }, { immediate: true })

    onBeforeUnmount(() => {
      if (isRegistrable.value) observer!.unregister(handle)
    })

    return {
      //store
      errorStore,
    }
  }
})
</script>

<template>
  <div
    class="l-unit__validationProvider"
    :data-validation-error="!!errorStore.getErrorMessagesByFieldName(field).length">
    <div class="l-unit__validationProvider--description">
      <slot />

      <FieldErrorMessage
        v-if="$slots.default"
        :field="field"
        :show-message="showMessage" />

      <slot name="secondary" />
    <!-- /.l-unit__validationProvider description --></div>
  <!-- /.l-unit__validationProvider --></div>
</template>
